import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Drawer,
  Card,
  CardContent,
  Divider,
  Link,
  TextField,
  Autocomplete,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { SourcePlatform } from "../SourcePlatform";
import apiService, {
  PrivateContent,
  PrivateBrandMention,
  PrivateBrandPresence,
  Program,
  ContentDailyMetricsResponse,
  ContentDailyMetricRow,
  LivestreamTimelineResponse,
} from "../../services/api";
import { getBrandLogoUrl } from "../../utils/brandLogoUtils";
import { MiContenidoEntityAvatar } from "../../miContenido/MiContenidoEntityAvatar";
import { patchPlatformContentItems } from "../../miContenido/patchPlatformContentItems";
import { getS3Url } from "../../utils/imageUtils";
import { useDebounce } from "../../utils/useDebounce";
import { LivestreamTimelineBlock } from "../story/LivestreamTimelineBlock";
import { AudienceRetentionTimelineBlock } from "../story/AudienceRetentionTimelineBlock";
import { ContentStatistics } from "./ContentStatistics";
import { ContentDailyMetricsGrowthSection } from "./ContentDailyMetricsGrowthSection";
import { ContentDetailDailyMetricsSection } from "./ContentDetailDailyMetricsSection";
import { FacebookEmbed } from "./FacebookEmbed";
import { appliesDailyMetricsMinDateFilter } from "../../miContenido/contentTrends/dailyTrendsConfig";
import type { DailyMetricPoint } from "../../pages/ProgramAnalysis/dailyMetricsChart";
import {
  isYouTubeLongFormMentionMode,
  showManualBrandPresenceSections,
} from "../../utils/contentShortForm";
import { getYoutubeVideoIdFromContent } from "../../utils/youtubeVideoId";
import { getMentionPositionLabel } from "../../utils/mentionPositionLabel";
import { facebookEmbedUrl } from "../../utils/facebookEmbedUrl";
import { useAuth } from "../../contexts/AuthContext";
import { ROUTES_NAMES } from "../Layout";

// Helper function to generate YouTube URL with timestamp
const getYouTubeUrlWithTimestamp = (
  content: PrivateContent,
  startTimeSeconds: number,
): string => {
  if (content.source === "youtube" && content.url) {
    const url = new URL(content.url);
    url.searchParams.set("t", Math.floor(startTimeSeconds).toString() + "s");
    return url.toString();
  }
  return content.url || "";
};

// Helper function to format seconds to MM:SS
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// Helper function to format large numbers (e.g., 1234 -> "1.2K")
const formatViewerCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

interface ContentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  content: PrivateContent | null;
  allBrands?: Array<{
    _id: string;
    name: string;
    logoUrl?: string;
    mentionCount: number;
    presenceCount: number;
  }>;
  brandPresence?: { [key: string]: PrivateBrandPresence[] };
  onBrandPresenceAdded?: () => void;
  selectedBrandId?: string | null;
  selectedBrandName?: string | null;
  /** When false, livestream timeline and viewer data are hidden (e.g. when company podcast has livestream data disabled). Default true. */
  showLivestreamData?: boolean;
  /** When true, show daily metrics charts (Mi contenido trends style). Defaults from route when omitted. */
  showDailyMetricsCharts?: boolean;
  /** Navigate to the previous item in the open list (ArrowLeft). */
  onNavigatePrevious?: () => void;
  /** Navigate to the next item in the open list (ArrowRight). */
  onNavigateNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

// Helper function to get embed URL for iframe
const getEmbedContent = (item: PrivateContent) => {
  switch (item.source) {
    case "instagram": {
      // Extract ID from URL for both regular posts and Reels
      let instagramId = "";
      if (item?.url?.includes("/reel/")) {
        instagramId = item.url.split("/reel/")[1]?.split("/")[0];
      } else if (item?.url?.includes("/p/")) {
        instagramId = item.url.split("/p/")[1]?.split("/")[0];
      }
      return `https://www.instagram.com/p/${instagramId}/embed`;
    }
    case "tiktok": {
      const tiktokId = item.url?.split("/video/")[1]?.split("?")[0];
      return `https://www.tiktok.com/embed/${tiktokId}`;
    }
    case "youtube": {
      if (item.mediaType === "short") {
        const videoId = item.url?.split("shorts/")[1]?.split("?")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      } else {
        // Regular YouTube video
        const videoId = item.url?.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    case "spotify": {
      // Extract Spotify ID from URL
      if (item.url?.includes("spotify.com/episode/")) {
        const episodeId = item.url.split("/episode/")[1]?.split("?")[0];
        return `https://open.spotify.com/embed/episode/${episodeId}`;
      } else if (item.url?.includes("spotify.com/show/")) {
        const showId = item.url.split("/show/")[1]?.split("?")[0];
        return `https://open.spotify.com/embed/show/${showId}`;
      }
      return item.url;
    }
    case "facebook":
      return facebookEmbedUrl(item.url);
    default:
      return item.url;
  }
};

const getContentType = (item: PrivateContent) => {
  if (item.source === "youtube") {
    return item.mediaType === "video" ? "YouTube Video" : "YouTube Short";
  } else if (item.source === "instagram") {
    return item.mediaType === "video" ? "Instagram Reel" : "Instagram Post";
  } else if (item.source === "tiktok") {
    return "TikTok Video";
  } else if (item.source === "spotify") {
    return "Spotify";
  }
  return `${item.source} ${item.mediaType}`;
};

export const ContentDetailsDialog: React.FC<ContentDetailsDialogProps> = ({
  open,
  onClose,
  content,
  allBrands,
  brandPresence,
  onBrandPresenceAdded,
  selectedBrandId,
  selectedBrandName,
  showLivestreamData = false,
  showDailyMetricsCharts: showDailyMetricsChartsProp,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isCampanasRoute = location.pathname.startsWith(
    `${ROUTES_NAMES.CAMPANAS}/`,
  );
  const isMiContenidoRoute = location.pathname.startsWith(
    ROUTES_NAMES.MI_CONTENIDO,
  );
  const showDailyMetricsCharts =
    showDailyMetricsChartsProp ?? (isCampanasRoute || isMiContenidoRoute);
  const showCampanasGrowthChartSection =
    showDailyMetricsCharts && isCampanasRoute;
  const showMiContenidoDailyMetricsSection =
    showDailyMetricsCharts && (showDailyMetricsChartsProp === true || isMiContenidoRoute);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [brandSearchValue, setBrandSearchValue] = useState("");
  const debouncedBrandSearch = useDebounce(brandSearchValue, 300);
  const [isAddingPresence, setIsAddingPresence] = useState(false);
  const [deletingPresenceId, setDeletingPresenceId] = useState<string | null>(
    null,
  );
  const [deletingContent, setDeletingContent] = useState(false);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState<string | null>(
    null,
  );
  const [programDraftId, setProgramDraftId] = useState<string | null>(null);
  const [isSavingProgram, setIsSavingProgram] = useState(false);

  const isDev = process.env.NODE_ENV === "development";
  const canDeletePrivateContent = isDev && content?.source === "instagram";

  // Initialize selectedBrandFilter based on selectedBrandId or selectedBrandName
  React.useEffect(() => {
    if (selectedBrandName) {
      setSelectedBrandFilter(selectedBrandName);
    } else if (selectedBrandId && allBrands) {
      const selectedBrand = allBrands.find(
        (brand) => brand._id === selectedBrandId,
      );
      if (selectedBrand) {
        setSelectedBrandFilter(selectedBrand.name);
      }
    } else {
      setSelectedBrandFilter(null);
    }
  }, [selectedBrandId, selectedBrandName, allBrands]);

  // Get unique brand names for filter
  const getUniqueBrandNames = (mentions: PrivateBrandMention[]): string[] => {
    const brandNames = mentions.map((mention) => mention.brandName);
    return Array.from(new Set(brandNames)).sort();
  };

  // Filter mentions based on selected brand
  const getFilteredMentions = (
    mentions: PrivateBrandMention[],
  ): PrivateBrandMention[] => {
    if (!selectedBrandFilter) return mentions;
    return mentions.filter(
      (mention) => mention.brandName === selectedBrandFilter,
    );
  };

  // Update filter when selectedBrandName prop changes
  useEffect(() => {
    setSelectedBrandFilter(selectedBrandName || null);
  }, [selectedBrandName]);

  useEffect(() => {
    if (!open) return;

    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return Boolean(target.closest("[contenteditable='true']"));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "ArrowLeft") {
        if (!hasPrevious || !onNavigatePrevious) return;
        event.preventDefault();
        onNavigatePrevious();
      } else if (event.key === "ArrowRight") {
        if (!hasNext || !onNavigateNext) return;
        event.preventDefault();
        onNavigateNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    hasPrevious,
    hasNext,
    onNavigatePrevious,
    onNavigateNext,
  ]);

  const syncContentId = content?._id;
  const syncProgramId = content?.programId;
  useEffect(() => {
    if (open && syncContentId != null) {
      const pid =
        syncProgramId != null && String(syncProgramId).trim() !== ""
          ? String(syncProgramId)
          : null;
      setProgramDraftId(pid);
    }
  }, [open, syncContentId, syncProgramId]);

  // Fetch private brand mentions for this content (only for YouTube videos)
  const { data: brandMentions } = useQuery<
    Record<string, PrivateBrandMention[]>
  >({
    queryKey: ["collections-private-brand-mentions", content?._id],
    queryFn: async () => {
      if (!content?._id) return {};
      return await apiService.getPrivateBrandMentions([content._id]);
    },
    enabled: !!content?._id && open && isYouTubeLongFormMentionMode(content),
    staleTime: 0, // 5 minutes
    gcTime: 0, // 5 minutes
  });

  const allMentionsForContent: PrivateBrandMention[] =
    (content && brandMentions?.[content._id]) || [];
  // Physical props stay in Placements de Marca. Banners/overlays render as
  // content mentions so their frames are visible in the mention cards.
  const placementMentions = allMentionsForContent.filter(
    (m) => m.placementType === "physical_placement",
  );
  const contentMentions = allMentionsForContent.filter(
    (m) => m.placementType !== "physical_placement",
  );

  const videoId = content ? getYoutubeVideoIdFromContent(content) : null;
  const isYouTubeLongForm = isYouTubeLongFormMentionMode(content);
  const hasAudienceRetention = Boolean(
    isYouTubeLongForm &&
      content?.duration &&
      content.duration > 0 &&
      content.insights?.audienceRetention?.length,
  );
  const timelineBrandMentions = useMemo(
    () =>
      content
        ? (brandMentions?.[content._id]?.map((m: PrivateBrandMention) => ({
            brandName: m.brandName,
            mentionStartTime: m.segmentStart,
            mentionType: m.mentionType ?? "neutral",
            s3Key: m.brand?.s3Key,
            logoUrl: m.brand?.logoUrl,
          })) ?? [])
        : [],
    [brandMentions, content],
  );

  const hasInlineDailyMetrics = Boolean(content?.dailyMetrics?.length);

  const needsDailyMetricsFetch =
    open &&
    !!content?._id &&
    (showMiContenidoDailyMetricsSection ||
      (showCampanasGrowthChartSection &&
        isYouTubeLongForm &&
        !hasInlineDailyMetrics));

  const {
    data: fetchedDailyMetricsDoc,
    isLoading: dailyMetricsLoading,
  } = useQuery<ContentDailyMetricsResponse>({
    queryKey: ["content-daily-metrics", content?._id],
    queryFn: () => apiService.getContentDailyMetrics(content!._id),
    enabled: needsDailyMetricsFetch,
    staleTime: 60 * 1000,
  });

  const effectiveDailyMetrics = useMemo((): DailyMetricPoint[] => {
    const inlineRaw: ContentDailyMetricRow[] = content?.dailyMetrics ?? [];
    const fetchedRaw: ContentDailyMetricRow[] =
      fetchedDailyMetricsDoc?.dailyMetrics ?? [];
    const raw =
      fetchedRaw.length > inlineRaw.length ? fetchedRaw : inlineRaw;
    if (!raw.length) return [];
    return raw.map((m) => ({
      collectedAt:
        m.collectedAt instanceof Date
          ? m.collectedAt.toISOString()
          : String(m.collectedAt),
      daysSincePublished: m.daysSincePublished,
      publishedAt: content?.publishedAt,
      views: Number(m.views) || 0,
      likes: Number(m.likes) || 0,
      comments: Number(m.comments) || 0,
    }));
  }, [content?.dailyMetrics, content?.publishedAt, fetchedDailyMetricsDoc?.dailyMetrics]);

  const { data: catalogBrandsData, isLoading: catalogBrandsLoading } = useQuery(
    {
      queryKey: ["content-details-brands", debouncedBrandSearch],
      queryFn: () =>
        apiService.getBrands(1, 100, debouncedBrandSearch || undefined),
      enabled: open && !!content && showManualBrandPresenceSections(content),
      staleTime: 0,
      gcTime: 0,
    },
  );

  // Fetch livestream by video ID, or by Mongo id when enriched (same access pattern as collection reports)
  const { data: livestream, isLoading: livestreamLookupLoading } = useQuery({
    queryKey: ["livestream-by-video", videoId, content?.livestreamId],
    queryFn: async () => {
      if (videoId) {
        const row = await apiService.getLivestreamByVideoId(videoId);
        if (row) return row;
      }
      if (content?.livestreamId) {
        return apiService.getLivestreamReportSummaryByMongoId(
          String(content.livestreamId),
        );
      }
      return null;
    },
    enabled:
      (!!videoId || !!content?.livestreamId) &&
      open &&
      showLivestreamData &&
      isYouTubeLongFormMentionMode(content),
  });

  // Fetch livestream timeline for viewer counts at each mention time
  const { data: livestreamTimeline } = useQuery<
    LivestreamTimelineResponse | null
  >({
    queryKey: ["livestream-timeline", livestream?._id],
    queryFn: () => apiService.getLivestreamTimeline(livestream!._id, 60, true),
    enabled: !!livestream?._id && open && showLivestreamData,
  });

  // Effective livestream ID: from content (enriched) or from lookup by video ID
  const effectiveLivestreamId =
    content?.livestreamId ?? livestream?._id ?? null;
  const { data: livestreamById, isLoading: livestreamByIdLoading } = useQuery({
    queryKey: ["livestream", effectiveLivestreamId],
    queryFn: () => apiService.getLivestream(effectiveLivestreamId!),
    enabled: !!effectiveLivestreamId && open && showLivestreamData,
  });
  const { data: livestreamSnapshotsData, isLoading: snapshotsLoading } =
    useQuery({
      queryKey: ["livestream-snapshots", effectiveLivestreamId],
      queryFn: () => apiService.getLivestreamSnapshots(effectiveLivestreamId!),
      enabled: !!effectiveLivestreamId && open && showLivestreamData,
    });
  const timelineSnapshots = livestreamSnapshotsData?.snapshots ?? [];

  const { data: podcastProgramsData, isLoading: podcastProgramsLoading } =
    useQuery({
      queryKey: ["podcast-programs", content?.podcastId],
      queryFn: () => apiService.getPodcastPrograms(content!.podcastId),
      enabled: open && !!content?.podcastId,
      staleTime: 60 * 1000,
    });
  const podcastPrograms: Program[] = podcastProgramsData?.programs ?? [];
  const hasReleaseWindow = Boolean(
    livestreamById?.releaseTime &&
    livestreamById?.youtubeDuration != null &&
    livestreamById.youtubeDuration > 0,
  );
  const timelineTotalDuration = hasReleaseWindow
    ? livestreamById!.youtubeDuration!
    : (livestreamById?.totalDuration ??
      (livestreamById?.actualStartTime && livestreamById?.actualEndTime
        ? (new Date(livestreamById.actualEndTime).getTime() -
            new Date(livestreamById.actualStartTime).getTime()) /
          1000
        : 0));
  const hasTimelineReady =
    !!livestreamById &&
    timelineTotalDuration > 0 &&
    timelineSnapshots.length > 0;
  const canLookupLivestream =
    open &&
    showLivestreamData &&
    isYouTubeLongForm &&
    (!!videoId || !!content?.livestreamId);
  const livestreamLoading =
    canLookupLivestream &&
    (livestreamLookupLoading ||
      (!!effectiveLivestreamId &&
        (livestreamByIdLoading || snapshotsLoading)));
  const showLivestreamSection =
    showLivestreamData &&
    isYouTubeLongForm &&
    (livestreamLoading || hasTimelineReady);
  // Get livestream viewers at a specific timestamp — only livestream data, no retention fallback
  const getLiveViewersAtTime = (timeInSeconds: number): number | null => {
    if (!livestreamTimeline?.segments?.length) return null;
    const segment = livestreamTimeline.segments.find(
      (s) => timeInSeconds >= s.startSeconds && timeInSeconds < s.endSeconds,
    );
    return segment?.viewers != null ? segment.viewers : null;
  };

  const CLOSEST_VIEWERS_WINDOW_SECONDS = 3 * 60; // ~3 minutes

  // Get viewers from closest segment when exact time has no data (within ~5 min)
  const getClosestLiveViewers = (
    timeInSeconds: number,
  ): { viewers: number; atTime: number } | null => {
    if (!livestreamTimeline?.segments?.length) return null;
    const withViewers = livestreamTimeline.segments.filter(
      (s) => s.viewers != null,
    );
    if (!withViewers.length) return null;
    const withDistance = withViewers.map((s) => {
      let dist: number;
      if (timeInSeconds >= s.startSeconds && timeInSeconds < s.endSeconds)
        dist = 0;
      else if (timeInSeconds < s.startSeconds)
        dist = s.startSeconds - timeInSeconds;
      else dist = timeInSeconds - s.endSeconds;
      return { segment: s, dist };
    });
    const withinWindow = withDistance.filter(
      (x) => x.dist <= CLOSEST_VIEWERS_WINDOW_SECONDS,
    );
    if (!withinWindow.length) return null;
    withinWindow.sort((a, b) => a.dist - b.dist);
    const best = withinWindow[0];
    return best
      ? { viewers: best.segment.viewers!, atTime: best.segment.startSeconds }
      : null;
  };

  const handleClose = () => {
    setSelectedBrand(null);
    setBrandSearchValue("");
    setIsAddingPresence(false);
    setProgramDraftId(null);
    onClose();
  };

  const savedProgramId =
    content?.programId != null && String(content.programId).trim() !== ""
      ? String(content.programId)
      : null;
  const programDirty = !!content && programDraftId !== savedProgramId;

  const handleSaveProgram = async () => {
    if (!content || !programDirty) return;
    setIsSavingProgram(true);
    try {
      const result = await apiService.updatePrivateContentProgram(
        content._id,
        programDraftId,
      );
      const nextProgramId = result.programId;
      const selectedProgram = nextProgramId
        ? podcastPrograms.find((p) => p._id === nextProgramId)
        : undefined;

      const previous =
        queryClient.getQueryData<PrivateContent>([
          "private-content-by-id",
          content._id,
        ]) ?? content;
      const nextDetail: PrivateContent = result.content
        ? { ...previous, ...result.content, programId: nextProgramId ?? undefined }
        : {
            ...previous,
            programId: nextProgramId ?? undefined,
          };
      if (!nextProgramId) {
        delete nextDetail.programId;
      }
      queryClient.setQueryData(["private-content-by-id", content._id], nextDetail);

      patchPlatformContentItems(queryClient, content._id, (item) => ({
        ...item,
        programId: nextProgramId,
        programName: selectedProgram?.name ?? null,
        programImageS3Key: selectedProgram?.imageS3Key ?? null,
        programImageUpdatedAt: null,
      }));

      queryClient.invalidateQueries({ queryKey: ["podcast-brands"] });
      queryClient.invalidateQueries({ queryKey: ["collections-content"] });
      queryClient.invalidateQueries({ queryKey: ["tab-content"] });
      queryClient.invalidateQueries({ queryKey: ["tab-counts"] });
      queryClient.invalidateQueries({
        queryKey: ["private-content-by-id", content._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["private-content", content._id],
      });
      queryClient.invalidateQueries({ queryKey: ["platform-content"] });
      if (onBrandPresenceAdded) {
        onBrandPresenceAdded();
      }
    } catch (error) {
      console.error("Error updating program:", error);
    } finally {
      setIsSavingProgram(false);
    }
  };

  const handleAddBrandPresence = async () => {
    if (!selectedBrand || !content) return;

    setIsAddingPresence(true);
    try {
      console.log("Adding brand presence:", {
        contentId: content._id,
        brandId: selectedBrand._id,
        brandName: selectedBrand.name,
        s3Key: selectedBrand.s3Key,
      });

      await apiService.createPrivateBrandPresence(
        content._id,
        selectedBrand._id,
        selectedBrand.name,
        selectedBrand.s3Key,
      );

      console.log("✅ Brand presence added successfully");

      const brandKey = `id:${String(selectedBrand._id)}`;
      const logo =
        getBrandLogoUrl({
          s3Key: selectedBrand.s3Key,
          logoUrl: selectedBrand.logoUrl,
        }) || null;

      patchPlatformContentItems(queryClient, content._id, (item) => {
        const existing = item.brands ?? [];
        if (existing.some((b) => b.brandKey === brandKey)) {
          return item;
        }
        return {
          ...item,
          brands: [
            ...existing,
            {
              brandKey,
              name: String(selectedBrand.name ?? "Marca"),
              logo,
            },
          ],
        };
      });

      // Invalidate relevant queries to refresh brand data
      queryClient.invalidateQueries({ queryKey: ["podcast-brands"] });
      queryClient.invalidateQueries({ queryKey: ["collections-content"] });
      queryClient.invalidateQueries({
        queryKey: ["collections-private-brand-mentions", content._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand-presence", content._id],
      });
      queryClient.invalidateQueries({ queryKey: ["platform-content"] });

      // Reset form and notify parent
      setSelectedBrand(null);
      setBrandSearchValue("");
      if (onBrandPresenceAdded) {
        onBrandPresenceAdded();
      }
    } catch (error) {
      console.error("❌ Error adding brand presence:", error);
    } finally {
      setIsAddingPresence(false);
    }
  };

  const handleDeleteBrandPresence = async (presenceId: string) => {
    setDeletingPresenceId(presenceId);
    try {
      console.log("Deleting brand presence:", presenceId);

      const presence = content
        ? brandPresence?.[content._id]?.find((p) => p._id === presenceId)
        : undefined;

      await apiService.deletePrivateBrandPresence(presenceId);

      console.log("✅ Brand presence deleted successfully");

      if (content && presence) {
        const brandKey =
          presence.brandId != null && String(presence.brandId).trim() !== ""
            ? `id:${String(presence.brandId)}`
            : `name:${(presence.brandName || "")
                .trim()
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{M}/gu, "")}`;
        patchPlatformContentItems(queryClient, content._id, (item) => {
          const existing = item.brands ?? [];
          const next = existing.filter((b) => b.brandKey !== brandKey);
          if (next.length === existing.length) return item;
          return { ...item, brands: next };
        });
      }

      // Invalidate relevant queries to refresh brand data
      queryClient.invalidateQueries({ queryKey: ["podcast-brands"] });
      queryClient.invalidateQueries({ queryKey: ["collections-content"] });
      queryClient.invalidateQueries({
        queryKey: ["collections-private-brand-mentions", content?._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand-presence", content?._id],
      });
      queryClient.invalidateQueries({ queryKey: ["platform-content"] });

      if (onBrandPresenceAdded) {
        onBrandPresenceAdded();
      }
    } catch (error) {
      console.error("❌ Error deleting brand presence:", error);
    } finally {
      setDeletingPresenceId(null);
    }
  };

  const handleDeletePrivateContent = async () => {
    if (!content?._id || !canDeletePrivateContent) return;
    if (
      !window.confirm(
        "¿Eliminar este contenido de Instagram y todos sus datos relacionados? Esta acción no se puede deshacer.",
      )
    )
      return;
    setDeletingContent(true);
    try {
      await apiService.deletePrivateContent(content._id);
      queryClient.invalidateQueries({ queryKey: ["tab-content"] });
      queryClient.invalidateQueries({ queryKey: ["tab-counts"] });
      queryClient.invalidateQueries({ queryKey: ["collections-content"] });
      handleClose();
    } catch (error) {
      console.error("Error deleting private content:", error);
    } finally {
      setDeletingContent(false);
    }
  };

  if (!content) return null;

  const embedUrl = getEmbedContent(content);
  const selectedProgramOption: Program | null = programDraftId
    ? (podcastPrograms.find((p) => p._id === programDraftId) ?? null)
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "40%",
          minWidth: 400,
          maxWidth: 600,
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h2">
            Detalles del Contenido
            {process.env.NODE_ENV === "development" && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 2, color: "text.secondary", fontFamily: "monospace" }}
              >
                ID: {content._id}
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {(onNavigatePrevious || onNavigateNext) && (
              <>
                <Tooltip title="Anterior (←)">
                  <span>
                    <IconButton
                      onClick={onNavigatePrevious}
                      disabled={!hasPrevious || !onNavigatePrevious}
                      size="small"
                      aria-label="Contenido anterior"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Siguiente (→)">
                  <span>
                    <IconButton
                      onClick={onNavigateNext}
                      disabled={!hasNext || !onNavigateNext}
                      size="small"
                      aria-label="Contenido siguiente"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {canDeletePrivateContent && (
              <Tooltip title="Eliminar contenido y datos relacionados (solo dev)">
                <span>
                  <IconButton
                    onClick={handleDeletePrivateContent}
                    disabled={deletingContent}
                    size="small"
                    color="error"
                    aria-label="Eliminar contenido"
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Stack spacing={3}>
          {/* Content Type and Title Section */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SourcePlatform
                contentSource={content.source}
                showLabel={false}
                iconSize={20}
              />
              <Typography variant="subtitle1">
                {getContentType(content)}
              </Typography>
              {content.url && (
                <Link
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: 0.5,
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </Link>
              )}
            </Box>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {content.title}
            </Typography>
          </Box>

          {/* Embed/Video Section - use native iframe to avoid triggering Instagram embed.js invariant when drawer opens */}
          <Card>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
                p: content.source === "facebook" ? 1 : 0,
              }}
            >
              {content.source === "facebook" ? (
                <FacebookEmbed
                  url={content.url}
                  title={content.title}
                  mediaType={content.mediaType}
                />
              ) : embedUrl ? (
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <iframe
                  key={embedUrl}
                  src={embedUrl}
                  title={content.title || "Embed"}
                  frameBorder={0}
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  style={{
                    width:
                      content.source === "tiktok"
                        ? "300px"
                        : content.source === "instagram"
                          ? "400px"
                          : "100%",
                    height:
                      content.source === "spotify"
                        ? 352
                        : content.source === "tiktok"
                          ? 600
                          : 500,
                    border: "none",
                    borderRadius: 4,
                    overflow: "hidden",
                  }}
                />
                </Box>
              ) : null}
            </Box>
          </Card>

          {/* Live viewers timeline (when content has livestream data: from content.livestreamId or lookup by video ID) */}
          {showLivestreamSection ? (
            <LivestreamTimelineBlock
              videoId={videoId ?? undefined}
              livestreamById={livestreamById}
              timelineSnapshots={timelineSnapshots}
              timelineTotalDuration={timelineTotalDuration}
              brandMentions={timelineBrandMentions}
              isLoading={livestreamLoading && !hasTimelineReady}
            />
          ) : null}

          {hasAudienceRetention && content ? (
            <AudienceRetentionTimelineBlock
              durationSeconds={content.duration!}
              audienceRetention={content.insights!.audienceRetention!}
              averageViewPercentage={content.insights?.averageViewPercentage}
              averageViewDuration={content.insights?.averageViewDuration}
              brandMentions={timelineBrandMentions}
            />
          ) : null}

          {content.podcastId ? (
            <Box>
              <Typography variant="h3" sx={{ mb: 1.5 }}>
                Programa
              </Typography>
              {programDraftId &&
              !selectedProgramOption &&
              !podcastProgramsLoading ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Programa asignado no disponible en la lista actual.
                </Typography>
              ) : null}
              <Autocomplete<Program, false, false, false>
                options={podcastPrograms}
                loading={podcastProgramsLoading}
                value={selectedProgramOption}
                onChange={(_, v) => setProgramDraftId(v?._id ?? null)}
                getOptionLabel={(p) => p.name}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Programa"
                    placeholder="Seleccionar programa..."
                    size="small"
                  />
                )}
              />
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setProgramDraftId(null)}
                  disabled={programDraftId === null}
                >
                  Sin programa
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveProgram}
                  disabled={!programDirty || isSavingProgram}
                >
                  {isSavingProgram ? "Guardando..." : "Guardar"}
                </Button>
              </Stack>
            </Box>
          ) : null}

          {showMiContenidoDailyMetricsSection && content._id ? (
            <ContentDetailDailyMetricsSection
              dailyMetrics={effectiveDailyMetrics}
              applyMinDateFilter={appliesDailyMetricsMinDateFilter(content)}
              isLoading={
                needsDailyMetricsFetch &&
                dailyMetricsLoading &&
                effectiveDailyMetrics.length === 0
              }
            />
          ) : null}

          {showCampanasGrowthChartSection && isYouTubeLongForm && content._id ? (
            <ContentDailyMetricsGrowthSection
              contentId={content._id}
              dailyMetrics={effectiveDailyMetrics}
            />
          ) : null}

          <Divider />

          <ContentStatistics
            key={String(content._id)}
            content={content}
            title="Métricas"
            readOnly
          />

          {/* Visual Placements (physical props only; banners render as mentions) */}
          {content &&
            isYouTubeLongFormMentionMode(content) &&
            placementMentions.length > 0 && (
              <Box>
                <Tooltip
                  title="Marca visible físicamente en escena: producto en escritorio, packaging, taza, prop, señalética o backdrop en el set. No incluye ropa/indumentaria ni banners (esos aparecen en Menciones de Marcas)."
                  arrow
                  placement="top-start"
                >
                  <Typography
                    variant="h3"
                    sx={{ mb: 2, display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "help" }}
                  >
                    Placements de Marca
                  </Typography>
                </Tooltip>
                <Stack spacing={1}>
                  {placementMentions
                    .slice()
                    .sort(
                      (a: PrivateBrandMention, b: PrivateBrandMention) =>
                        a.segmentStart - b.segmentStart,
                    )
                    .map((mention: PrivateBrandMention) => (
                      <Card key={mention._id} variant="outlined">
                        <CardContent
                          sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <MiContenidoEntityAvatar
                              name={mention.brandName}
                              src={
                                mention.brand
                                  ? getBrandLogoUrl(mention.brand)
                                  : null
                              }
                              size={32}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {mention.brandName}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                </Stack>
              </Box>
            )}

          {/* Brand Mentions (only for YouTube videos) */}
          {content &&
            isYouTubeLongFormMentionMode(content) &&
            contentMentions.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h3">
                    Menciones de Marcas{" "}
                    {selectedBrandFilter &&
                      `(${getFilteredMentions(contentMentions).length})`}
                  </Typography>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 200 }}
                    options={[
                      "Todas las marcas",
                      ...getUniqueBrandNames(contentMentions),
                    ]}
                    value={
                      selectedBrandFilter
                        ? selectedBrandFilter
                        : "Todas las marcas"
                    }
                    onChange={(_, newValue) => {
                      setSelectedBrandFilter(
                        newValue === "Todas las marcas" ? null : newValue,
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Filtrar por marca"
                        variant="outlined"
                      />
                    )}
                  />
                </Box>
                <Stack spacing={1}>
                  {getFilteredMentions(contentMentions)
                    .sort(
                      (a: PrivateBrandMention, b: PrivateBrandMention) =>
                        a.segmentStart - b.segmentStart,
                    )
                    .map((mention: PrivateBrandMention) => (
                      <Card key={mention._id} variant="outlined">
                        <CardContent
                          sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {/* Logo, Brand Name, and Link Row */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <MiContenidoEntityAvatar
                                  name={mention.brandName}
                                  src={
                                    mention.brand
                                      ? getBrandLogoUrl(mention.brand)
                                      : null
                                  }
                                  size={32}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {mention.brandName}
                                  {process.env.NODE_ENV === "development" && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      ({mention._id})
                                    </Typography>
                                  )}
                                </Typography>
                                {mention.placementType === "banner" && (
                                  <Chip
                                    size="small"
                                    label={getMentionPositionLabel(mention)}
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                              </Box>

                              {/* Time, Live Viewers (only when livestream data exists), and YouTube Link Button */}
                              {content.source === "youtube" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Tooltip
                                    title="Minuto de la mención"
                                    arrow
                                    placement="top"
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <AccessTimeIcon
                                        sx={{
                                          fontSize: "0.75rem",
                                          color: "text.secondary",
                                          opacity: 0.8,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {formatTime(mention.segmentStart)}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                  {(() => {
                                    const atStart = getLiveViewersAtTime(
                                      mention.segmentStart,
                                    );
                                    const atEnd = getLiveViewersAtTime(
                                      mention.segmentEnd,
                                    );
                                    const closest = getClosestLiveViewers(
                                      mention.segmentStart,
                                    );
                                    const liveViewers =
                                      atStart ?? atEnd ?? closest?.viewers;
                                    if (liveViewers == null) return null;
                                    const timeUsed =
                                      atStart != null
                                        ? mention.segmentStart
                                        : atEnd != null
                                          ? mention.segmentEnd
                                          : closest!.atTime;
                                    return (
                                      <Tooltip
                                        title={`Personas viendo en el momento de la mención (${formatTime(timeUsed)}): ${liveViewers.toLocaleString()}`}
                                        arrow
                                        placement="top"
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <VisibilityIcon
                                            sx={{
                                              fontSize: "0.85rem",
                                              color: "primary.main",
                                              opacity: 0.8,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            color="primary.main"
                                            sx={{
                                              fontWeight: 600,
                                              backgroundColor: "primary.50",
                                              py: 0.25,
                                              borderRadius: 0.5,
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {formatViewerCount(liveViewers)}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                  <Tooltip
                                    title="Ver momento en YouTube"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const youtubeUrl =
                                          getYouTubeUrlWithTimestamp(
                                            content,
                                            mention.segmentStart,
                                          );
                                        window.open(youtubeUrl, "_blank");
                                      }}
                                      sx={{
                                        color: "primary.main",
                                        "&:hover": {
                                          backgroundColor: "primary.50",
                                        },
                                      }}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>

                            {/* Description and Video Row */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                              }}
                            >
                              {/* Description */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {mention.context
                                    ? `"${mention.context.substring(0, 150)}${mention.context.length > 150 ? "..." : ""}"`
                                    : "Mención detectada"}
                                </Typography>
                              </Box>

                              {/* Video clip of the mention */}
                              {mention.videoS3Key && (
                                <Box sx={{ flexShrink: 0 }}>
                                  <video
                                    controls
                                    poster={
                                      mention.framesS3Key
                                        ? getS3Url(mention.framesS3Key[2])
                                        : undefined
                                    }
                                    style={{
                                      width: "200px",
                                      height: "112px",
                                      borderRadius: "8px",
                                      border: "1px solid #e0e0e0",
                                      objectFit: "cover",
                                    }}
                                    preload="none"
                                  >
                                    <source
                                      src={getS3Url(mention.videoS3Key)}
                                      type="video/mp4"
                                    />
                                    Tu navegador no soporta el elemento de
                                    video.
                                  </video>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                </Stack>
              </Box>
            )}

          {/* Current Brand Presences (only for non-YouTube videos) */}
          {brandPresence &&
            content &&
            showManualBrandPresenceSections(content) &&
            brandPresence[content._id] &&
            brandPresence[content._id].length > 0 && (
              <Box>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  Marcas Presentes
                </Typography>
                <Stack spacing={1}>
                  {brandPresence[content._id].map(
                    (presence: PrivateBrandPresence, index: number) => {
                      const pt = presence.placementType;
                      return (
                      <Card key={presence._id ?? index} variant="outlined">
                        <CardContent
                          sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <MiContenidoEntityAvatar
                              name={presence.brandName}
                              src={
                                presence.brand?.s3Key
                                  ? getBrandLogoUrl({
                                      s3Key: presence.brand.s3Key,
                                    })
                                  : null
                              }
                              size={32}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {presence.brandName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {pt === "tagged"
                                  ? "Etiquetado / caption"
                                  : pt === "overlay"
                                    ? "Logo de marca en pantalla"
                                    : pt === "placement"
                                      ? "Placement físico"
                                      : presence.presenceType ===
                                          "brand_mention"
                                        ? "Mención de marca"
                                        : "Presencia detectada"}
                              </Typography>
                            </Box>
                            {presence.verified && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  backgroundColor: "success.main",
                                  borderRadius: "50%",
                                }}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteBrandPresence(presence._id)
                              }
                              disabled={deletingPresenceId === presence._id}
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  backgroundColor: "error.50",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                      );
                    },
                  )}
                </Stack>
              </Box>
            )}

          {/* Brand Presence Section (only for non-YouTube videos) */}
          {content && showManualBrandPresenceSections(content) && (
            <Box>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Agregar Presencia de Marca
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={catalogBrandsData?.brands ?? []}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  loading={catalogBrandsLoading}
                  getOptionLabel={(option) => option.name}
                  value={selectedBrand}
                  onChange={(_, newValue) => setSelectedBrand(newValue)}
                  inputValue={brandSearchValue}
                  onInputChange={(_, newInputValue) =>
                    setBrandSearchValue(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar marca"
                      placeholder="Escribe para buscar una marca..."
                      fullWidth
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <MiContenidoEntityAvatar
                        name={option.name}
                        src={getBrandLogoUrl(option) || null}
                        size={24}
                      />
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.totalMentions} menciones
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText="No se encontraron marcas"
                  loadingText="Cargando marcas..."
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddBrandPresence}
                disabled={!selectedBrand || isAddingPresence}
              >
                {isAddingPresence
                  ? "Agregando..."
                  : "Agregar Presencia de Marca"}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};
